/**
 * Modèles Angular pour la gestion
 * des factures fournisseurs Dolibarr.
 *
 * @author jlg
 * 8 août 2026
 */


/**
 * Facture fournisseur Dolibarr.
 */
export class SupplierInvoiceModel {

  /**
   * Identifiant Dolibarr.
   */
  id: number;

  /**
   * Référence de la pièce Dolibarr.
   */
  ref: string;

  /**
   * Identifiant du fournisseur Dolibarr.
   */
  supplierId: number;

  /**
   * Nom du fournisseur.
   */
  supplierName: string;

  /**
   * Date de la pièce.
   *
   * Format standard d'échange :
   * YYYY-MM-DD HH:mm:ss
   */
  invoiceDate: string;

  /**
   * Date d'échéance.
   *
   * Format standard d'échange :
   * YYYY-MM-DD HH:mm:ss
   */
  dueDate: string;

  /**
   * Montant HT.
   */
  totalHT: number;

  /**
   * Montant TVA.
   */
  totalTVA: number;

  /**
   * Montant TTC.
   */
  totalTTC: number;

  /**
   * Devise.
   */
  currency: string;

  /**
   * Statut Dolibarr.
   *
   * Conservé uniquement pour les données
   * provenant de Dolibarr.
   */
  status: number;

  /**
   * Statut de paiement Dolibarr.
   */
  paymentStatus: number;

  /**
   * Statut de suivi de la facture électronique.
   *
   * DEPOSEE
   * REJETEE
   * REFUSEE
   * ENCAISSEE
   */
  trackingStatus: string;

  /**
   * Statut électronique provenant de Dolibarr.
   */
  electronicStatus: SupplierInvoiceElectronicStatusModel;

  /**
   * Type de pièce Dolibarr.
   */
  documentType: string;

  /**
   * Indique si un PDF est disponible.
   */
  pdfAvailable: boolean;

  /**
   * Nom du fichier PDF.
   */
  pdfName: string;


  constructor() {

    this.id = null;

    this.ref = null;

    this.supplierId = null;

    this.supplierName = null;

    this.invoiceDate = null;

    this.dueDate = null;

    this.totalHT = null;

    this.totalTVA = null;

    this.totalTTC = null;

    this.currency = null;

    this.status = null;

    this.paymentStatus = null;

    this.trackingStatus = null;

    this.electronicStatus = null;

    this.documentType = null;

    this.pdfAvailable = false;

    this.pdfName = null;
  }
}


/**
 * Statut électronique d'une facture fournisseur.
 */
export class SupplierInvoiceElectronicStatusModel {

  /**
   * Identifiant de l'enregistrement de statut.
   */
  rowid: number;

  /**
   * Code du statut électronique.
   */
  code: number;

  /**
   * Libellé technique du statut.
   */
  status: string;

  /**
   * Information complémentaire.
   */
  info: string;

  /**
   * Indique si le statut a été transmis.
   */
  transmitted: number;

  /**
   * Indique si un statut a déjà été transmis.
   */
  everTransmitted: number;

  /**
   * Identifiant du flux électronique.
   */
  flowId: string;

  /**
   * Fournisseur de la plateforme électronique.
   */
  provider: string;


  constructor() {

    this.rowid = null;

    this.code = null;

    this.status = null;

    this.info = null;

    this.transmitted = null;

    this.everTransmitted = null;

    this.flowId = null;

    this.provider = null;
  }
}


/**
 * Critères de recherche des factures fournisseurs.
 */
export class SupplierInvoiceSearchModel {

  /**
   * Identifiant du fournisseur Dolibarr.
   */
  supplierId: number;

  /**
   * Date de début de recherche.
   */
  dateFrom: Date;

  /**
   * Date de fin de recherche.
   */
  dateTo: Date;

  /**
   * Recherche sur la référence de pièce.
   */
  search: string;


  constructor() {

    this.supplierId = null;

    this.dateFrom = null;
    this.dateTo = null;

    this.search = null;
  }
}


/**
 * Fournisseur Dolibarr.
 */
export class SupplierModel {

  /**
   * Identifiant Dolibarr.
   */
  id: number;

  /**
   * Nom du fournisseur.
   */
  name: string;


  constructor() {

    this.id = null;
    this.name = null;
  }
}


/**
 * Suivi d'une facture.
 *
 * Correspond à InvoiceTracking
 * côté serveur.
 */
export class InvoiceTrackingModel {

  /**
   * Identifiant du suivi.
   */
  id: string;

  /**
   * Entité métier.
   */
  stdEntity: string;

  /**
   * Type de document.
   */
  documentType: string;

  /**
   * Système source.
   */
  sourceSystem: string;

  /**
   * Identifiant de la facture.
   */
  invoiceId: string;

  /**
   * Référence de la facture.
   */
  invoiceReference: string;

  /**
   * Identifiant du partenaire métier.
   */
  businessPartnerId: string;

  /**
   * Système externe.
   */
  externalSystem: string;

  /**
   * Identifiant externe.
   */
  externalId: string;

  /**
   * Statut du tracking.
   */
  trackingStatus: string;

  /**
   * Date du statut.
   */
  statusDate: Date;

  /**
   * Dernière erreur.
   */
  lastError: string;

  /**
   * Date de création.
   */
  stdCreation: Date;

  /**
   * Date de dernière modification.
   */
  stdLastupdate: Date;

  /**
   * Date de désactivation.
   */
  stdDisable: Date;


  constructor() {

    this.id = null;

    this.stdEntity = null;
    this.documentType = null;
    this.sourceSystem = null;

    this.invoiceId = null;
    this.invoiceReference = null;

    this.businessPartnerId = null;

    this.externalSystem = null;
    this.externalId = null;

    this.trackingStatus = null;

    this.statusDate = null;
    this.lastError = null;

    this.stdCreation = null;
    this.stdLastupdate = null;
    this.stdDisable = null;
  }
}


/**
 * Événement du tracking d'une facture.
 *
 * Correspond à InvoiceTrackingEvent
 * côté serveur.
 */
export class InvoiceTrackingEventModel {

  /**
   * Identifiant de l'événement.
   */
  id: string;

  /**
   * Identifiant du tracking.
   */
  invoiceTrackingId: string;

  /**
   * Type d'événement.
   */
  eventType: string;

  /**
   * Date de l'événement.
   */
  eventDate: string;

  /**
   * Système source.
   */
  source: string;

  /**
   * Identifiant utilisateur.
   */
  userId: string;

  /**
   * Message.
   */
  message: string;

  /**
   * Données techniques.
   *
   * Correspond au JSONB côté PostgreSQL,
   * mais est transmis ici sous forme de chaîne JSON.
   */
  technicalData: string;

  /**
   * Date de création.
   */
  stdCreation: string;


  constructor() {

    this.id = null;

    this.invoiceTrackingId = null;

    this.eventType = null;
    this.eventDate = null;

    this.source = null;
    this.userId = null;

    this.message = null;
    this.technicalData = null;

    this.stdCreation = null;
  }
  
}
export interface InvoiceStatusDefinition {
    code: number;
    frenchLabel: string;
    customer: boolean;
    supplier: boolean;
    requiresReason: boolean;
  }

export const INVOICE_STATUSES: InvoiceStatusDefinition[] = [
{ code: 200, frenchLabel: 'Déposée', customer: true, supplier: false, requiresReason: false },
{ code: 201, frenchLabel: 'Émise par la plateforme', customer: true, supplier: false, requiresReason: false },
{ code: 202, frenchLabel: 'Reçue par la plateforme du destinataire', customer: true, supplier: true, requiresReason: false },
{ code: 203, frenchLabel: 'Mise à disposition du client', customer: true, supplier: false, requiresReason: false },
{ code: 204, frenchLabel: 'Prise en charge', customer: true, supplier: false, requiresReason: false },
{ code: 205, frenchLabel: 'Approuvée', customer: true, supplier: true, requiresReason: false },
{ code: 206, frenchLabel: 'Approuvée partiellement', customer: true, supplier: false, requiresReason: false },
{ code: 207, frenchLabel: 'En litige', customer: true, supplier: false, requiresReason: true },
{ code: 208, frenchLabel: 'Suspendue', customer: true, supplier: false, requiresReason: true },
{ code: 209, frenchLabel: 'Complétée', customer: true, supplier: false, requiresReason: false },
{ code: 210, frenchLabel: 'Refusée', customer: true, supplier: true, requiresReason: true },
{ code: 211, frenchLabel: 'Paiement transmis', customer: true, supplier: false, requiresReason: false },
{ code: 212, frenchLabel: 'Encaissée', customer: true, supplier: false, requiresReason: false },
{ code: 213, frenchLabel: 'Rejetée', customer: true, supplier: false, requiresReason: false }
];

export interface SupplierInvoiceStatusReason {
  code: string;
  statuses: string[];
}

export const SUPPLIER_INVOICE_STATUS_REASONS:
  SupplierInvoiceStatusReason[] = [
  {code: 'NON_TRANSMISE', statuses: ['200']},
  {code: 'JUSTIF_ABS', statuses: ['208']},
  {code: 'ROUTAGE_ERR', statuses: ['221']},
  {code: 'AUTRE', statuses: ['207', '210']},
  {code: 'COORD_BANC_ERR', statuses: ['207', '210']},
  {code: 'TX_TVA_ERR', statuses: ['207', '210']},
  {code: 'MONTANTTOTAL_ERR', statuses: ['207', '210', '213']},
  {code: 'CALCUL_ERR', statuses: ['207', '210', '213']},
  {code: 'NON_CONFORME', statuses: ['207', '210', '213']},
  {code: 'DOUBLON', statuses: ['207', '210']},
  {code: 'DEST_INC', statuses: ['207', '213']},
  {code: 'DEST_ERR', statuses: ['207', '210', '213']},
  {code: 'TRANSAC_INC', statuses: ['207', '210']},
  {code: 'EMMET_INC', statuses: ['207', '210']},
  {code: 'CONTRAT_TERM', statuses: ['207', '210']},
  {code: 'DOUBLE_FACT', statuses: ['207', '210']},
  {code: 'CMD_ERR', statuses: ['207', '210']},
  {code: 'ADR_ERR', statuses: ['207', '210']},
  {code: 'SIRET_ERR', statuses: ['207', '213']},
  {code: 'CODE_ROUTAGE_ERR', statuses: ['207']},
  {code: 'REF_CT_ABSENT', statuses: ['207', '208', '210']},
  {code: 'REF_ERR', statuses: ['207']},
  {code: 'PU_ERR', statuses: ['206', '207']},
  {code: 'REM_ERR', statuses: ['207']},
  {code: 'QTE_ERR', statuses: ['206', '207']},
  {code: 'ART_ERR', statuses: ['207']},
  {code: 'MODPAI_ERR', statuses: ['207']},
  {code: 'QUALITE_ERR', statuses: ['207']},
  {code: 'LIVR_INCOMP', statuses: ['206', '207']},
  {code: 'REJ_SEMAN', statuses: ['213']},
  {code: 'REJ_UNI', statuses: ['213']},
  {code: 'REJ_COH', statuses: ['213']},
  {code: 'REJ_ADR', statuses: ['213']},
  {code: 'REJ_CONT_B2G', statuses: ['213']},
  {code: 'REJ_REF_PJ', statuses: ['213']},
  {code: 'REJ_ASS_PJ', statuses: ['213']},
  {code: 'IRR_VIDE_F', statuses: ['213']},
  {code: 'IRR_TYPE_F', statuses: ['213']},
  {code: 'IRR_SYNTAX', statuses: ['213']},
  {code: 'IRR_TAILLE_PJ', statuses: ['213']},
  {code: 'IRR_NOM_PJ', statuses: ['213']},
  {code: 'IRR_VID_PJ', statuses: ['213']},
  {code: 'IRR_EXT_DOC', statuses: ['213']},
  {code: 'IRR_TAILLE_F', statuses: ['213']},
  {code: 'IRR_ANTIVIRUS', statuses: ['213']}

];


